import type { TCreateSlice, TAsyncStateWithProgressMessage } from "@/store";

import type { TTableLocation } from "@/apis/odin/apiODIN";

export type TDocumentType = "operating_statement" | "rent_roll" | "other";

type TSuccessPayload = {
    extractionId: number;
    allFoundTables: TTableLocation[];
};

type TUploadError = {
    errorMessage: string;
    isExtractionNetworkError?: boolean;
};

type TStateSliceAddExtraction = {
    extractionState: TAsyncStateWithProgressMessage<
        TSuccessPayload,
        TUploadError
    >;
};

type TActionsPublicSliceAddExtraction = {
    startExtraction: () => void;
    extractionProgress: (params: {
        progress: number;
        progressMessage: string;
    }) => void;
    extractionSuccess: (params: TSuccessPayload) => void;
    extractionFailure: (payload: TUploadError) => void;
    resetStateSliceAddExtraction: () => void;
};

export type TSliceAddExtraction = TStateSliceAddExtraction &
    TActionsPublicSliceAddExtraction;

export const initialStateSliceAddExtraction = {
    extractionState: {
        status: "idle",
        progress: null,
        progressMessage: null,
        data: null,
        error: null,
    },
} satisfies TStateSliceAddExtraction;

export const createSliceAddExtraction: TCreateSlice<TSliceAddExtraction> =
    () => set => {
        const startExtraction: TActionsPublicSliceAddExtraction["startExtraction"] =
            () => {
                set(
                    state => ({
                        ...state,
                        extractionState: {
                            ...state.extractionState,
                            data: null,
                            error: null,
                            status: "pending",
                        },
                    }),
                    false,
                    "sliceAddExtraction/startExtraction",
                );
            };
        const extractionProgress: TActionsPublicSliceAddExtraction["extractionProgress"] =
            payload =>
                set(
                    state => ({
                        ...state,
                        extractionState: {
                            ...state.extractionState,
                            progress: payload.progress,
                            progressMessage: payload.progressMessage,
                        },
                    }),
                    false,
                    "sliceAddExtraction/extractionProgress",
                );
        const extractionSuccess: TActionsPublicSliceAddExtraction["extractionSuccess"] =
            payload =>
                set(
                    state => ({
                        ...state,
                        extractionState: {
                            ...state.extractionState,
                            status: "resolved",
                            error: null,
                            data: payload,
                        },
                    }),
                    false,
                    "sliceAddExtraction/extractionSuccess",
                );
        const extractionFailure: TActionsPublicSliceAddExtraction["extractionFailure"] =
            payload =>
                set(
                    state => ({
                        ...state,
                        extractionState: {
                            ...state.extractionState,
                            status: "rejected",
                            error: payload,
                            data: null,
                            progress: null,
                            progressMessage: null,
                        },
                    }),
                    false,
                    "sliceAddExtraction/extractionFailure",
                );
        const resetStateSliceAddExtraction: TActionsPublicSliceAddExtraction["resetStateSliceAddExtraction"] =
            () =>
                set(
                    { ...initialStateSliceAddExtraction },
                    false,
                    "sliceAddExtraction/resetStateSliceAddExtraction",
                );

        return {
            ...initialStateSliceAddExtraction,
            startExtraction,
            extractionProgress,
            extractionSuccess,
            extractionFailure,
            resetStateSliceAddExtraction,
        };
    };
